import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import camaApi from '../api/camaApi';

export const useCamas = () => {
  const [camas, setCamas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener todas las camas
  const fetchCamas = useCallback(async (habitacionId = null, estadoCamaId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.getCamas(habitacionId, estadoCamaId);
      setCamas(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener camas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener camas con estadísticas
  const fetchCamasWithStats = useCallback(async (habitacionId = null, estadoCamaId = null, moduloId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.getCamasWithStats(habitacionId, estadoCamaId, moduloId);
      setCamas(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener camas con estadísticas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener cama por ID
  const fetchCamaById = useCallback(async (camaId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.getCamaById(camaId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener cama';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener camas por habitación
  const fetchCamasByHabitacion = useCallback(async (habitacionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.getCamasByHabitacion(habitacionId);
      setCamas(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener camas de la habitación';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener camas libres por habitación
  const fetchCamasLibresByHabitacion = useCallback(async (habitacionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.getCamasLibresByHabitacion(habitacionId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener camas libres';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener camas ocupadas por habitación
  const fetchCamasOcupadasByHabitacion = useCallback(async (habitacionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.getCamasOcupadasByHabitacion(habitacionId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener camas ocupadas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear cama
  const createCama = useCallback(async (camaData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.createCama(camaData);
      setCamas(prev => [...prev, response.data]);
      toast.success('Cama creada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al crear cama';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Actualizar cama
  const updateCama = useCallback(async (camaId, camaData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.updateCama(camaId, camaData);
      setCamas(prev => 
        prev.map(cama => 
          cama.id === camaId ? response.data : cama
        )
      );
      toast.success('Cama actualizada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al actualizar cama';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Eliminar cama
  const deleteCama = useCallback(async (camaId) => {
    setLoading(true);
    setError(null);
    
    try {
      await camaApi.deleteCama(camaId);
      setCamas(prev => prev.filter(cama => cama.id !== camaId));
      toast.success('Cama eliminada exitosamente');
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al eliminar cama';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar camas
  const searchCamas = useCallback(async (searchTerm, habitacionId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await camaApi.searchCamas(searchTerm, habitacionId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al buscar camas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const cambiarEstadoCama = useCallback(async (camaId, activo) => {
    setLoading(true);
    setError(null);
    try {
      const response = await camaApi.cambiarEstadoCama(camaId, activo);
      setCamas(prev => prev.map(c => (c.id === camaId ? { ...c, ...response.data } : c)));
      toast.success(activo ? 'Cama activada exitosamente' : 'Cama inactivada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al cambiar el estado de la cama';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

    // Limpiar error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Estado
    camas,
    loading,
    error,
    
    // Acciones
    fetchCamas,
    fetchCamasWithStats,
    fetchCamaById,
    fetchCamasByHabitacion,
    fetchCamasLibresByHabitacion,
    fetchCamasOcupadasByHabitacion,
    createCama,
    updateCama,
    deleteCama,
    cambiarEstadoCama,
    searchCamas,
    clearError,
  };
};
